import type { ReactNode } from "react";

import { Calendar, Clock } from "./icons";
import { cn } from "./cn";
import { controlClassName } from "./controlClassName";

type PickerKind = "date" | "time" | "datetime-local";

export interface PickerProps {
  id?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

// The native indicator is stretched over the whole field and made invisible,
// so a tap anywhere still opens the picker while only our icon is visible.
const triggerClassName = cn(
  controlClassName,
  "relative pr-11",
  "[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0",
  "[&::-webkit-calendar-picker-indicator]:size-full [&::-webkit-calendar-picker-indicator]:cursor-pointer",
  "[&::-webkit-calendar-picker-indicator]:opacity-0",
);

/**
 * The native control does the calendar work. It is the one input Telegram's
 * WebView renders with the platform's own accessible picker, so a hand-built
 * popover would trade away keyboard and screen-reader behaviour for nothing.
 * The token-styled shell and the trailing mark are ours.
 */
function Picker({
  kind,
  icon,
  id,
  value,
  onValueChange,
  placeholder,
  disabled,
  ...aria
}: PickerProps & { kind: PickerKind; icon: ReactNode }) {
  return (
    <div className="relative">
      <input
        id={id}
        type={kind}
        value={value ?? ""}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => {
          onValueChange?.(event.target.value);
        }}
        className={cn(triggerClassName, !value && "text-fg-subtle")}
        {...aria}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-fg-subtle"
      >
        {icon}
      </span>
    </div>
  );
}

export function DatePicker(props: PickerProps) {
  return <Picker kind="date" icon={<Calendar size={18} />} {...props} />;
}

export function TimePicker(props: PickerProps) {
  return <Picker kind="time" icon={<Clock size={18} />} {...props} />;
}

export function DateTimePicker(props: PickerProps) {
  return (
    <Picker kind="datetime-local" icon={<Calendar size={18} />} {...props} />
  );
}
